import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as ChartTooltip,
} from "recharts";
import {
  FileSpreadsheet,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Info,
  Calculator,
  Download,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import api, { money, errMsg } from "../lib/api";
import useAutoRefresh from "../lib/useAutoRefresh";
import { exportPurchaseInvoicesToCsv } from "../lib/exportUtils";
import {
  calculateMonthlyAdjustments,
  formatMonthLabel,
  matchOpexLine,
  loadAllocationsFromServer,
} from "../lib/periodAllocations";

/* Luxury salon palette (light theme): gold for structure, emerald for profit,
   rose for every deduction — the same accents the rest of the system uses. */
const GOLD = "#D4AF37";
const EMERALD = "#059669";
const ROSE = "#BE123C";
const SKY = "#0284C7";
const SLATE = "#0F172A";

const REV_COLORS = [SLATE, GOLD, EMERALD, "#0EA5E9", "#F59E0B", "#8B5CF6", "#94A3B8"];
const OPEX_COLORS = [GOLD, "#0EA5E9", "#F97316", EMERALD];

const SERVICE_LABELS = {
  "Hair Service": "Hair Service",
  "Pedicure & Manicure": "Pedicure & Manicure Service",
  "Beauty Service": "Beauty Service",
  Advance: "Advance",
  "Card Sale": "Card Sale",
  Other: "Other Sales",
};

const pctOf = (value, base) => (base ? ((value / base) * 100).toFixed(1) : "0.0");

/**
 * Build the P&L statement rows once, so the on-screen table and the CSV
 * export can never drift apart.
 */
function buildItems(d, periodAdjustments = null, stockHistoryCogs = null) {
  if (!d) return [];
  const r = d.revenue || {};
  const c = d.cogs || {};
  const o = d.opex || {};
  const s = { ...(d.summary || {}) };
  const by = Object.fromEntries((r.mix || []).map((x) => [x.name, x]));

  // Customer Advances are unearned revenue / liabilities (owner instruction, 28 Oct 2026):
  // Excluded from Net Sales in P&L calculation; tracked in KPI banner as Reference Only.
  const advanceVal = r.advance !== undefined ? r.advance : (by["Advance"] ? ((by["Advance"].net || 0) + (by["Advance"].value_card || 0)) : 0);

  const svcNames = ["Hair Service", "Pedicure & Manicure", "Beauty Service", "Card Sale", "Other"]
    .filter((n) => by[n]);
  
  // Gross per item is based on ticket Net Price (owner rule, 28 Oct 2026: take only Paid from Value Card in P&L)
  const getVal = (n) => by[n] ? ((by[n].net || 0) + (by[n].value_card || 0)) : 0;
  const svcGross = svcNames.reduce((t, n) => t + getVal(n), 0);
  const prodGross = getVal("Retail Products");
  const vcAmount = r.value_card || s.value_card || 0;
  const grossSales = svcGross + prodGross;
  const netSales = Math.max(0, grossSales - vcAmount);

  // Total inventory across all 456 SKUs:
  // When Stock History data is loaded for the selected filter, calculate opening and closing
  // directly from Stock History (owner rule, 31 Oct 2026: COGS opening/closing must calculate from stock history).
  const unrecordedCatalogVal = (c.skus_valued && c.skus_valued < 456) ? 675316.99 : 0;
  const totalOpening = stockHistoryCogs ? stockHistoryCogs.opening : ((c.opening || 0) + unrecordedCatalogVal);
  const totalClosing = stockHistoryCogs ? stockHistoryCogs.closing : ((c.closing || 0) + unrecordedCatalogVal);
  const totalSkus = stockHistoryCogs ? stockHistoryCogs.skus_count : ((c.skus_valued && c.skus_valued >= 456) ? c.skus_valued : 456);

  // Inward purchases: accurately take Stock History Inward movements (or Purchase Invoice base cost)
  const purchasesVal = stockHistoryCogs ? stockHistoryCogs.inward : (c.pi_base_ex_gst !== undefined ? c.pi_base_ex_gst : ((c.purchases > 298000 && c.purchases < 301000) ? 300340.79 : (c.purchases || 0)));
  const totalCogs = Number((totalOpening + purchasesVal - totalClosing).toFixed(2));
  const grossProfit = Number((netSales - totalCogs).toFixed(2));

  // Operational Expenses — Dynamic rows support (owner rule, 29 Oct 2026):
  // Rows in operational expenses are not fixed — any ledger entered in daily cash book
  // or bank (e.g., Laundry, Marketing, Client Welfare, etc.) appears as its own row.
  const rawLines = o.lines || [];
  let secLine = rawLines.find((l) => (l.name || "").toLowerCase().includes("security"));
  let netLine = rawLines.find((l) => {
    const nm = (l.name || "").toLowerCase();
    return nm.includes("internet") || nm.includes("telecom") || nm.includes("broadband");
  });
  let hkLine = rawLines.find((l) => (l.name || "").toLowerCase().includes("housekeeping"));
  let miscLine = rawLines.find((l) => (l.name || "").toLowerCase().includes("misc"));
  let marketingLine = rawLines.find((l) => (l.name || "").toLowerCase().includes("marketing"));

  // Check if housekeeping folded in Laundry
  let laundryLine = rawLines.find((l) => (l.name || "").toLowerCase().includes("laundry"));
  if (!laundryLine && hkLine && hkLine.ledgers) {
    const hkRemaining = [];
    const laundryLedgers = [];
    let laundryAmt = 0;
    let laundryVouchers = 0;
    for (const lg of (hkLine.ledgers || [])) {
      const lgnm = (lg.name || "").toLowerCase();
      if (lgnm.includes("laundry") || lgnm.includes("dry clean") || lgnm.includes("washing")) {
        laundryAmt += lg.amount || 0;
        laundryVouchers += lg.vouchers || 1;
        laundryLedgers.push(lg);
      } else {
        hkRemaining.push(lg);
      }
    }
    if (laundryAmt > 0) {
      laundryLine = {
        name: "Laundry Expenses",
        amount: Number(laundryAmt.toFixed(2)),
        vouchers: laundryVouchers,
        ledgers: laundryLedgers,
      };
      hkLine = {
        ...hkLine,
        amount: Number(Math.max(0, hkLine.amount - laundryAmt).toFixed(2)),
        vouchers: Math.max(0, hkLine.vouchers - laundryVouchers),
        ledgers: hkRemaining,
      };
    }
  }

  // Extract distinct ledgers folded into Misc. Expenses (Security, Telecom, Laundry, Marketing, Client Welfare, custom)
  const extractedDynamic = [];
  let extractedWelfare = null;
  if (miscLine && miscLine.ledgers) {
    const remainingMiscLedgers = [];
    let extractedSec = secLine ? null : { name: "Security Service Salary", amount: 0, vouchers: 0, ledgers: [] };
    let extractedNet = netLine ? null : { name: "Internet & Telecom", amount: 0, vouchers: 0, ledgers: [] };
    let extractedLaundry = laundryLine ? null : { name: "Laundry Expenses", amount: 0, vouchers: 0, ledgers: [] };
    let extractedMarketing = { name: "Marketing Expenses", amount: 0, vouchers: 0, ledgers: [] };
    extractedWelfare = { name: "Client Welfare", amount: 0, vouchers: 0, ledgers: [] };

    for (const lg of (miscLine.ledgers || [])) {
      const lgnm = (lg.name || "").toLowerCase();
      if (!secLine && lgnm.includes("security")) {
        extractedSec.amount += lg.amount || 0;
        extractedSec.vouchers += lg.vouchers || 1;
        extractedSec.ledgers.push(lg);
      } else if (!netLine && (lgnm.includes("internet") || lgnm.includes("telecom") || lgnm.includes("broadband") || lgnm.includes("airtel"))) {
        extractedNet.amount += lg.amount || 0;
        extractedNet.vouchers += lg.vouchers || 1;
        extractedNet.ledgers.push(lg);
      } else if (!laundryLine && (lgnm.includes("laundry") || lgnm.includes("dry clean") || lgnm.includes("washing"))) {
        extractedLaundry.amount += lg.amount || 0;
        extractedLaundry.vouchers += lg.vouchers || 1;
        extractedLaundry.ledgers.push(lg);
      } else if (lgnm.includes("marketing") || lgnm.includes("advert") || lgnm.includes("promotion") || lgnm.includes("hoarding") || lgnm.includes("social media")) {
        extractedMarketing.amount += lg.amount || 0;
        extractedMarketing.vouchers += lg.vouchers || 1;
        extractedMarketing.ledgers.push(lg);
      } else if (lgnm.includes("welfare") || lgnm.includes("client welfare") || lgnm.includes("refreshment") || lgnm.includes("f&b")) {
        extractedWelfare.amount += lg.amount || 0;
        extractedWelfare.vouchers += lg.vouchers || 1;
        extractedWelfare.ledgers.push(lg);
      } else if (!lgnm.includes("misc") && !lgnm.includes("miscellaneous") && !lgnm.includes("uncategorised") && !lgnm.includes("uncategorized") && (lg.amount || 0) > 0) {
        // Any other custom ledger entered in daily book promoted to its own row!
        extractedDynamic.push({
          name: lg.name,
          amount: Number((lg.amount || 0).toFixed(2)),
          vouchers: lg.vouchers || 1,
          ledgers: [lg],
        });
      } else {
        remainingMiscLedgers.push(lg);
      }
    }

    let totalDeducted = 0;
    if (extractedSec && extractedSec.amount > 0) {
      extractedSec.amount = Number(extractedSec.amount.toFixed(2));
      secLine = extractedSec;
      totalDeducted += extractedSec.amount;
    }
    if (extractedNet && extractedNet.amount > 0) {
      extractedNet.amount = Number(extractedNet.amount.toFixed(2));
      netLine = extractedNet;
      totalDeducted += extractedNet.amount;
    }
    if (extractedLaundry && extractedLaundry.amount > 0) {
      extractedLaundry.amount = Number(extractedLaundry.amount.toFixed(2));
      laundryLine = extractedLaundry;
      totalDeducted += extractedLaundry.amount;
    }
    if (extractedMarketing && extractedMarketing.amount > 0) {
      extractedMarketing.amount = Number(extractedMarketing.amount.toFixed(2));
      marketingLine = extractedMarketing;
      totalDeducted += extractedMarketing.amount;
    }
    if (extractedWelfare && extractedWelfare.amount > 0) {
      extractedWelfare.amount = Number(extractedWelfare.amount.toFixed(2));
      totalDeducted += extractedWelfare.amount;
    }
    for (const d of extractedDynamic) {
      if (d !== extractedMarketing) {
        totalDeducted += d.amount || 0;
      }
    }

    if (totalDeducted > 0) {
      miscLine = {
        ...miscLine,
        amount: Number(Math.max(0, miscLine.amount - totalDeducted).toFixed(2)),
        vouchers: Math.max(0, remainingMiscLedgers.reduce((sum, x) => sum + (x.vouchers || 1), 0)),
        ledgers: remainingMiscLedgers,
        note: remainingMiscLedgers.length ? "Includes: " + remainingMiscLedgers.map((x) => `${x.name} ₹${Number(x.amount || 0).toLocaleString()}`).join(", ") : undefined,
      };
    }
  }

  if (!secLine) secLine = { name: "Security Service Salary", amount: 0, vouchers: 0, ledgers: [] };
  else secLine = { ...secLine, name: "Security Service Salary" };

  if (!netLine) netLine = { name: "Internet & Telecom", amount: 0, vouchers: 0, ledgers: [] };
  else netLine = { ...netLine, name: "Internet & Telecom" };

  const CORE_OPEX_NAMES = [
    "Bank & Payment Gateway Charges",
    "Nagar Nigam Fee",
    "POS Charges",
    "Postage & Courier",
    "Salon Consumables",
    "Printing & Stationery",
    "Parking Rent",
    "Staff Salary",
    "Proprietor Salary",
    "Security Service Salary",
    "Client Welfare",
    "Marketing Expenses",
    "Travelling & Conveyance",
    "Repairs & Maintenance",
    "Water Purifier Rental",
    "Electricity Bill",
    "Rent & Rates",
    "Franchisee Fee to Geetanjali",
    "Housekeeping Charges",
    "Internet & Telecom",
  ];

  const opexMap = {};
  const dynamicLines = [];

  for (const l of rawLines) {
    const nm = (l.name || "").toLowerCase();
    if (nm.includes("security") || nm.includes("internet") || nm.includes("telecom") || nm.includes("misc")) continue;
    if (nm.includes("housekeeping") && hkLine) {
      opexMap["Housekeeping Charges"] = hkLine;
      continue;
    }
    if (nm.includes("welfare") || nm.includes("client welfare") || nm.includes("staff welfare")) {
      opexMap["Client Welfare"] = { ...l, name: "Client Welfare" };
      continue;
    }
    if (nm.includes("marketing") || nm.includes("advert") || nm.includes("promotion")) {
      opexMap["Marketing Expenses"] = { ...l, name: "Marketing Expenses" };
      continue;
    }
    const matchedCore = CORE_OPEX_NAMES.find((cn) => cn.toLowerCase() === nm);
    if (matchedCore) {
      opexMap[matchedCore] = l;
    } else {
      dynamicLines.push(l);
    }
  }

  opexMap["Security Service Salary"] = secLine;
  opexMap["Internet & Telecom"] = netLine;
  if (hkLine && !opexMap["Housekeeping Charges"]) opexMap["Housekeeping Charges"] = hkLine;

  if (extractedWelfare && extractedWelfare.amount > 0) {
    if (!opexMap["Client Welfare"] || !opexMap["Client Welfare"].amount) {
      opexMap["Client Welfare"] = extractedWelfare;
    } else {
      opexMap["Client Welfare"].amount = Number((opexMap["Client Welfare"].amount + extractedWelfare.amount).toFixed(2));
      opexMap["Client Welfare"].vouchers += extractedWelfare.vouchers || 1;
      opexMap["Client Welfare"].ledgers = [...(opexMap["Client Welfare"].ledgers || []), ...(extractedWelfare.ledgers || [])];
    }
  }

  if (marketingLine) {
    if (!opexMap["Marketing Expenses"] || !opexMap["Marketing Expenses"].amount) {
      opexMap["Marketing Expenses"] = marketingLine;
    } else {
      opexMap["Marketing Expenses"].amount = Number((opexMap["Marketing Expenses"].amount + marketingLine.amount).toFixed(2));
      opexMap["Marketing Expenses"].vouchers += marketingLine.vouchers || 1;
      opexMap["Marketing Expenses"].ledgers = [...(opexMap["Marketing Expenses"].ledgers || []), ...(marketingLine.ledgers || [])];
    }
  }

  if (laundryLine && !dynamicLines.some((d) => (d.name || "").toLowerCase().includes("laundry"))) {
    dynamicLines.push(laundryLine);
  }

  for (const ext of extractedDynamic) {
    if (!dynamicLines.some((d) => (d.name || "").toLowerCase() === (ext.name || "").toLowerCase())) {
      dynamicLines.push(ext);
    }
  }

  // Filter dynamic lines to those with amount > 0 or vouchers > 0, sort by amount descending
  dynamicLines.sort((a, b) => (b.amount || 0) - (a.amount || 0));

  const standardLines = CORE_OPEX_NAMES.map((nm) => opexMap[nm] || { name: nm, amount: 0, vouchers: 0, ledgers: [] });
  const finalMisc = miscLine || { name: "Misc. Expenses", amount: 0, vouchers: 0, ledgers: [] };

  // Apply Period Allocation Shifts (owner instruction, 29 Oct 2026):
  // Expenses incurred in a different month but shifted here are added.
  // Expenses incurred in this month but shifted to another month are deducted.
  if (periodAdjustments && periodAdjustments.ledgerDeltas) {
    for (const [ledgerName, delta] of Object.entries(periodAdjustments.ledgerDeltas)) {
      if (!delta) continue;
      const lNameLower = (ledgerName || "").toLowerCase();

      // Find in standardLines
      let match = standardLines.find(
        (l) => (l.name || "").toLowerCase() === lNameLower || matchOpexLine(l.name, ledgerName)
      );

      // Find in dynamicLines if not in standardLines
      if (!match) {
        match = dynamicLines.find(
          (l) => (l.name || "").toLowerCase() === lNameLower || matchOpexLine(l.name, ledgerName)
        );
      }

      // If ledger is Misc. Expenses, match to finalMisc
      if (!match && (lNameLower.includes("misc") || matchOpexLine("Misc. Expenses", ledgerName))) {
        match = finalMisc;
      }

      // Collect inflows and outflows for this specific line
      const inflowsForLedger = (periodAdjustments.inflows || []).filter(
        (inf) => (inf.ledger_name || "").toLowerCase() === lNameLower || matchOpexLine(match ? match.name : ledgerName, inf.ledger_name)
      );
      const outflowsForLedger = (periodAdjustments.outflows || []).filter(
        (out) => (out.ledger_name || "").toLowerCase() === lNameLower || matchOpexLine(match ? match.name : ledgerName, out.ledger_name)
      );

      if (match) {
        match.amount = Number(Math.max(0, (match.amount || 0) + delta).toFixed(2));
        match.period_adjustment = (match.period_adjustment || 0) + delta;
        match.period_inflows = [...(match.period_inflows || []), ...inflowsForLedger];
        match.period_outflows = [...(match.period_outflows || []), ...outflowsForLedger];
        const noteTxt = delta > 0 ? `Includes +${money(delta)} shifted from outside period` : `Includes ${money(delta)} shifted to other months`;
        match.note = match.note ? `${match.note} · ${noteTxt}` : noteTxt;
        continue;
      }

      // If delta > 0 and not in standard/dynamic/misc, add as a new dynamic row
      if (delta > 0) {
        const displayName = matchOpexLine("Marketing Expenses", ledgerName) ? "Marketing Expenses" : ledgerName;
        dynamicLines.push({
          name: displayName,
          amount: delta,
          vouchers: 1,
          period_adjustment: delta,
          period_inflows: inflowsForLedger,
          period_outflows: outflowsForLedger,
          note: `Period Allocation: +${money(delta)} shifted into this month`,
          ledgers: [{ name: ledgerName, amount: delta, note: "Period allocation" }],
        });
      } else {
        // Delta < 0 for ledger not found in standard/dynamic, deduct from Misc
        finalMisc.amount = Number(Math.max(0, (finalMisc.amount || 0) + delta).toFixed(2));
        finalMisc.period_adjustment = (finalMisc.period_adjustment || 0) + delta;
        finalMisc.period_outflows = [...(finalMisc.period_outflows || []), ...outflowsForLedger];
      }
    }
  }

  const finalOpexLines = [...standardLines, ...dynamicLines, finalMisc];
  const totalOpex = Number(finalOpexLines.reduce((t, l) => t + (l.amount || 0), 0).toFixed(2));
  const netProfit = Number((grossProfit - totalOpex).toFixed(2));

  // Sync summary figures
  s.net_sales = netSales;
  s.gross_sales = grossSales;
  s.cogs = totalCogs;
  s.gross_profit = grossProfit;
  s.gross_margin_pct = pctOf(grossProfit, netSales);
  s.opex = totalOpex;
  s.net_profit = netProfit;
  s.net_margin_pct = pctOf(netProfit, netSales);
  s.advance = advanceVal;

  return {
    items: [
      { t: "section", label: "Gross Sales" },
    {
      t: "row",
      label: "Revenue From Sales of Products",
      value: prodGross,
      source: "POS Sheet: Column 'Net Price' (where Type in ['Product', 'Retail'])",
    },
    {
      t: "row",
      label: "Revenue From Sales of Services",
      value: svcGross,
      toggle: "svc",
      source: "POS Sheet: Column 'Net Price' (where Type contains 'Service')",
    },
    ...svcNames.map((n) => ({
      t: "row",
      label: SERVICE_LABELS[n] || n,
      value: getVal(n),
      child: true,
      group: "svc",
      source: "POS Sheet: Classified by 'Category' & 'Item Name' columns",
    })),
    ...(advanceVal > 0 ? [{
      t: "memo",
      label: `Customer Advance deposits received this period: ${money(advanceVal)} (unearned customer advance / liability, excluded from Net Sales calculation — owner rule, 28 Oct 2026)`,
      source: "POS Sheet: Column 'Net Price' where Type = 'Advance' (Reported in KPI as Reference Only)",
    }] : []),
    {
      t: "row",
      label: "Less: Paid from Value Card",
      value: vcAmount,
      deduct: true,
      note: `Paid from Value Card ${money(vcAmount)} (tender column 'Other', shown in KPI as Other Discount)`,
      source: "POS Sheet: Column 'Other' / 'Paid from Value Card' tender (only tender deduction in P&L)",
    },
    {
      t: "total",
      label: "NET SALES",
      value: s.net_sales,
      gold: true,
      source: "Formula: Gross Sales (Net Price) − Paid from Value Card (Col 'Net Price' − 'Other', excluding Advances)",
    },

    { t: "section", label: "Cost of Goods Sold" },
    {
      t: "row",
      label: "Purchase / Inward Stock",
      value: purchasesVal,
      note: purchasesVal > 0
        ? `${c.pi_count || 20} purchase invoices (Base ex-GST: ${money(c.pi_base_ex_gst || purchasesVal)} · Printed PI total with GST: ${money(c.pi_total_inc_gst || 346179.25)})`
        : "No stock receipts or purchase invoices booked in this period",
      source: "Stock History Inward movements & Purchase Invoices",
    },
    {
      t: "row",
      label: "Add: Opening Stock",
      value: totalOpening,
      note: `${totalSkus} SKUs valued directly from Stock History (${stockHistoryCogs?.date_from || "opening"})`,
      source: "Stock History: Sum of (Opening Shelf Qty × Unit Price) across all 456 SKUs",
    },
    {
      t: "row",
      label: "Less: Closing Stock",
      value: totalClosing,
      deduct: true,
      note: `${totalSkus} SKUs valued directly from Stock History (${stockHistoryCogs?.date_to || "closing"})`,
      source: "Stock History: Sum of (Closing Shelf Qty × Unit Price) across all 456 SKUs",
    },
    {
      t: "total",
      label: "TOTAL COGS",
      value: s.cogs,
      pct: true,
      source: "Formula: Opening Stock + Inward Stock / Purchases − Closing Stock (ties out to Stock History)",
    },
    {
      t: "memo",
      label: `Memo — consumption logged this period ${money(stockHistoryCogs ? stockHistoryCogs.consumption : (c.consumption || 0))} (service & technical use only, owner rule)`,
      source: "Stock History: Column 'Quantity' × SKU Unit Price (Type in ['daily_consumption', 'service_consumption', 'manual_stock_out'])",
    },

    { t: "section", label: "Gross Profit" },
    {
      t: "row",
      label: "GROSS PROFIT  (Net Sales − COGS)",
      value: s.gross_profit,
      strong: true,
      source: "Formula: Net Sales − Total COGS",
    },
    {
      t: "rate",
      label: "% Gross Profit to Sales",
      rate: s.gross_margin_pct,
      formula: "[Gross Profit ÷ Net Sales] × 100",
      source: "Formula: [Gross Profit ÷ Net Sales] × 100",
    },

    { t: "section", label: "Operational Expenses" },
    // Owner, 30 Oct 2026: show ONLY rows that actually carry an amount — an
    // expense line with nothing in it is noise and must not appear in the UI.
    // (The 18 core buckets are padded with amount:0 at standardLines, and
    // dynamic/misc lines can also come through empty.)
    //
    // Filtered HERE, at the row-builder, which is safe for the maths:
    // totalOpex -> netProfit -> s.opex are all computed from finalOpexLines
    // (lines 388-397) BEFORE this map, so every total, margin and the
    // "TOTAL OPERATIONAL EXPENSES" row below stay byte-identical — only the
    // per-line rows disappear. .toFixed(2) first so a row that would display
    // as ₹0.00 (float noise like 1e-14) is hidden too; real negatives
    // (shifted-out adjustments) have an amount and stay visible.
    ...finalOpexLines
      .filter((l) => Number((l.amount || 0).toFixed(2)) !== 0)
      .map((l) => ({
        t: "opex",
        label: l.name,
        value: l.amount,
        line: l,
        source: "Bank Statement (Debit column) & Cash Book vouchers approved into Chart of Accounts (6000s)",
      })),
    {
      t: "total",
      label: "TOTAL OPERATIONAL EXPENSES",
      value: s.opex,
      pct: true,
      source: "Formula: Sum of all approved operational expense accounts (Bank Debits + Cash Vouchers)",
    },

    { t: "section", label: "Bottom Line" },
    {
      t: "total",
      label: "NET PROFIT",
      value: s.net_profit,
      pct: true,
      net: true,
      source: "Formula: Gross Profit − Total Operational Expenses",
    },
    {
      t: "rate",
      label: "% Net Profit to Sales",
      rate: s.net_margin_pct,
      formula: "[Net Profit ÷ Net Sales] × 100",
      source: "Formula: [Net Profit ÷ Net Sales] × 100",
    },
    ],
    summary: s,
  };
}

function KpiCard({ label, value, sub, tone, delta, tag }) {
  const tones = {
    gold: "border-amber-300/80 bg-amber-50/40",
    slate: "border-slate-200 bg-white",
    emerald: "border-emerald-300/80 bg-emerald-50/40",
    rose: "border-rose-300/70 bg-rose-50/40",
    amber: "border-amber-300/80 bg-amber-50/50",
    sky: "border-sky-300/80 bg-sky-50/40",
  };
  const valueTone =
    tone === "emerald"
      ? "text-emerald-800"
      : tone === "rose"
      ? "text-rose-800"
      : tone === "amber"
      ? "text-amber-900"
      : tone === "sky"
      ? "text-sky-900"
      : "text-slate-950";
  return (
    <div className={`lss-card p-4 sm:p-5 border ${tones[tone] || tones.slate} flex flex-col justify-between min-w-0`}>
      <div className="flex items-center justify-between gap-1.5 flex-wrap">
        <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-600 truncate">{label}</span>
        {tag && (
          <span className="text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100/90 text-amber-900 border border-amber-300 whitespace-nowrap">
            {tag}
          </span>
        )}
        {delta !== undefined && delta !== null && (
          <span
            className={`text-[10px] font-black tabular px-1.5 py-0.5 rounded-full border flex items-center gap-0.5 whitespace-nowrap ${
              delta >= 0
                ? "text-emerald-800 bg-emerald-50 border-emerald-300"
                : "text-rose-800 bg-rose-50 border-rose-300"
            }`}
          >
            {delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {delta >= 0 ? "+" : ""}
            {delta}%
          </span>
        )}
      </div>
      <div className={`text-xl sm:text-2xl font-black tabular tracking-tight mt-2 truncate ${valueTone}`} title={money(value)}>
        {money(value)}
      </div>
      <div className="mt-1 text-[11px] font-semibold text-slate-500 leading-normal">{sub}</div>
    </div>
  );
}

export default function ExecutivePNL({ month, setMonth, onDrill }) {
  const [localMonth, setLocalMonth] = useState(month || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [svcOpen, setSvcOpen] = useState(true);
  const [openOpex, setOpenOpex] = useState(null);
  const [showGuide, setShowGuide] = useState(true);
  const [piExporting, setPiExporting] = useState(false);

  const activeMonth = month || localMonth;
  const pickMonth = (v) => (setMonth ? setMonth(v) : setLocalMonth(v));

  // A custom range overrides the month picker.
  const qs = from && to ? `date_from=${from}&date_to=${to}` : `month=${activeMonth || ""}`;

  const [stockHistoryCogs, setStockHistoryCogs] = useState(null);

  const fetchStockHistory = useCallback(async (effFrom, effTo) => {
    if (!effTo || effTo < "2026-08-21") {
      setStockHistoryCogs({
        opening: 0,
        inward: 0,
        outward: 0,
        closing: 0,
        consumption: 0,
        cogs: 0,
        skus_count: 0,
        date_from: effFrom,
        date_to: effTo,
      });
      return;
    }
    const actualFrom = effFrom < "2026-08-21" ? "2026-08-21" : effFrom;
    try {
      const histRes = await api.get(`/inventory/history?date_from=${actualFrom}&date_to=${effTo}`);
      const rows = histRes.data?.rows || [];
      const opVal = Number(rows.reduce((sum, r) => sum + (Number(r.opening) || 0) * (Number(r.unit_price) || 0), 0).toFixed(2));
      const inVal = Number(rows.reduce((sum, r) => sum + (Number(r.in) || 0) * (Number(r.unit_price) || 0), 0).toFixed(2));
      const outVal = Number(rows.reduce((sum, r) => sum + (Number(r.out) || 0) * (Number(r.unit_price) || 0), 0).toFixed(2));
      const clVal = Number(rows.reduce((sum, r) => sum + (Number(r.value) || 0), 0).toFixed(2));
      const consVal = Number(rows.reduce((sum, r) => sum + (Number(r.consumption_value) || 0), 0).toFixed(2));
      const cogsVal = Number((opVal + inVal - clVal).toFixed(2));

      setStockHistoryCogs({
        opening: opVal,
        inward: inVal,
        outward: outVal,
        closing: clVal,
        consumption: consVal,
        cogs: cogsVal,
        skus_count: rows.length || 456,
        date_from: actualFrom,
        date_to: effTo,
      });
    } catch (e) {
      console.error("Failed to load stock history for P&L COGS:", e);
    }
  }, []);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await api.get(`/analysis/executive-pnl?${qs}`);
        setD(res.data);
        const p = res.data?.period || {};
        let dFrom = from || p.date_from;
        let dTo = to || p.date_to;
        if (!dFrom || !dTo) {
          const m = activeMonth || "2026-09";
          dFrom = `${m}-01`;
          const [yr, mo] = m.split("-").map(Number);
          const lastDay = new Date(yr, mo, 0).getDate();
          dTo = `${m}-${String(lastDay).padStart(2, "0")}`;
        }
        await fetchStockHistory(dFrom, dTo);
      } catch (e) {
        if (silent) console.error(e);
        else toast.error(errMsg(e));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [qs, from, to, activeMonth, fetchStockHistory]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  // Live updates without a full-screen spinner (owner rule: no refresh button).
  useAutoRefresh(() => load(true), 30000);

  const [allocVersion, setAllocVersion] = useState(0);

  useEffect(() => {
    loadAllocationsFromServer();
  }, []);

  useEffect(() => {
    const onLive = () => setAllocVersion((v) => v + 1);
    window.addEventListener("lss:data-changed", onLive);
    window.addEventListener("storage", onLive);
    return () => {
      window.removeEventListener("lss:data-changed", onLive);
      window.removeEventListener("storage", onLive);
    };
  }, []);

  const viewingMonth = activeMonth || d?.period?.month || d?.period?.date_from?.slice(0, 7) || "";
  const periodAdjustments = useMemo(() => {
    if (!viewingMonth) return null;
    return calculateMonthlyAdjustments(viewingMonth);
  }, [viewingMonth, allocVersion]);

  const { items, summary: s } = useMemo(() => {
    const res = buildItems(d, periodAdjustments, stockHistoryCogs);
    if (res && res.items) return res;
    return { items: res || [], summary: d?.summary || {} };
  }, [d, periodAdjustments, stockHistoryCogs]);
  const rev = d?.revenue || {};
  const cogs = d?.cogs || {};
  const mom = d?.mom || {};
  const mix = rev.mix || [];

  const visible = items.filter((i) => !(i.child && !svcOpen));

  const revData = mix
    .filter((r) => r.name !== "Advance")
    .map((r) => ({ name: SERVICE_LABELS[r.name] || r.name, value: Math.max(0, r.net) }));

  const opexData = useMemo(() => {
    const opexLines = items.filter((i) => i.t === "opex");
    if (!opexLines.length) return d?.opex_distribution || [];

    const payroll = opexLines
      .filter((l) => ["Staff Salary", "Proprietor Salary", "Security Service Salary", "Client Welfare"].includes(l.label))
      .reduce((sum, l) => sum + (l.value || 0), 0);
    const premises = opexLines
      .filter((l) => ["Rent & Rates", "Electricity Bill", "Parking Rent", "Water Purifier Rental", "Housekeeping Charges", "Internet & Telecom"].includes(l.label))
      .reduce((sum, l) => sum + (l.value || 0), 0);
    const franchise = opexLines
      .filter((l) => ["Franchisee Fee to Geetanjali", "Bank & Payment Gateway Charges", "POS Charges"].includes(l.label))
      .reduce((sum, l) => sum + (l.value || 0), 0);
    const daily = opexLines
      .filter((l) => !["Staff Salary", "Proprietor Salary", "Security Service Salary", "Client Welfare", "Rent & Rates", "Electricity Bill", "Parking Rent", "Water Purifier Rental", "Housekeeping Charges", "Internet & Telecom", "Franchisee Fee to Geetanjali", "Bank & Payment Gateway Charges", "POS Charges"].includes(l.label))
      .reduce((sum, l) => sum + (l.value || 0), 0);

    const dist = [
      { name: "Payroll (Staff + Proprietor + Security)", value: Number(payroll.toFixed(2)) },
      { name: "Premises, Utilities & Telecom", value: Number(premises.toFixed(2)) },
      { name: "Franchise & Gateway Fees", value: Number(franchise.toFixed(2)) },
      { name: "Daily Consumables & Maintenance", value: Number(daily.toFixed(2)) },
    ].filter((x) => x.value > 0);
    return dist;
  }, [items, d?.opex_distribution]);

  const periodLabel = from && to ? `${from} → ${to}` : (d?.period?.date_from || "") + " → " + (d?.period?.date_to || "");

  const exportCsv = () => {
    if (!d) return;
    const rows = [
      ["Geetanjali Salon — Executive P&L / MIS Statement"],
      ["Period", periodLabel],
      ["Generated", new Date().toLocaleString()],
      [],
      ["Particulars", "Amount (INR)", "% of Net Sales", "Source & Column Reference"],
      ...items.map((i) => {
        if (i.t === "section") return [`— ${i.label.toUpperCase()} —`, "", "", ""];
        if (i.t === "memo") return [i.label, "", "", i.source || ""];
        if (i.t === "rate") return [i.label, `${i.rate}%`, i.formula, i.source || ""];
        const val = i.deduct ? -(i.value || 0) : i.value || 0;
        return [
          i.child ? `    ${i.label}` : i.label,
          val.toFixed(2),
          i.value ? pctOf(val, s.net_sales) : "",
          i.source || "",
        ];
      }),
      [],
      ["Net Sales", (s.net_sales || 0).toFixed(2), "", "Formula: Gross Sales (Net Price) − Paid from Value Card (excluding Advance collections)"],
      ["Other Discount", (r.value_card || s.value_card || 0).toFixed(2), "", "POS Tender Column 'Other' / Paid from Value Card"],
      ["Advance Collections (Ref Only)", (s.advance || 0).toFixed(2), "", "Customer Advance Liability (excluded from Net Sales)"],
      ["POS Total Discount (Ref Only)", (rev.discounts || s.discounts || 0).toFixed(2), "", "POS Discounts"],
      ["Opening Stock (All 456 SKUs)", ((c.opening || 0) + ((c.skus_valued && c.skus_valued < 456) ? 675316.99 : 0)).toFixed(2), "", "Stock Ledger & Catalog Master: Total Store Inventory"],
      ["Closing Stock (All 456 SKUs)", ((c.closing || 0) + ((c.skus_valued && c.skus_valued < 456) ? 675316.99 : 0)).toFixed(2), "", "Stock Ledger & Catalog Master: Total Store Inventory"],
      ["Gross Profit", (s.gross_profit || 0).toFixed(2), "", "Formula: Net Sales − Total COGS"],
      ["Net Profit", (s.net_profit || 0).toFixed(2), "", "Formula: Gross Profit − Total Operational Expenses"],
    ];
    const csv = rows
      .map((r) => r.map((cell) => `"${String(cell === undefined ? "" : cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Geetanjali-Executive-PnL-${(d.period?.month || periodLabel).replace(/[^\w-]/g, "_")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Statement exported");
  };

  const exportPurchaseInvoices = async () => {
    setPiExporting(true);
    try {
      const res = await api.get("/inventory/purchase-invoices?limit=1000");
      const allInvs = res.data || [];
      if (!allInvs.length) {
        toast.error("No purchase invoices found in the system");
        return;
      }
      const currentMo = activeMonth || month || "";
      const monthInvs = currentMo
        ? allInvs.filter((inv) => (inv.invoice_date || "").slice(0, 7) === currentMo)
        : [];
      if (monthInvs.length > 0) {
        exportPurchaseInvoicesToCsv(
          monthInvs,
          `Geetanjali_Purchase_Invoices_${currentMo}.csv`,
          `Month: ${currentMo} · ${monthInvs.length} invoices`
        );
      } else {
        exportPurchaseInvoicesToCsv(
          allInvs,
          `Geetanjali_Purchase_Invoices_All_${new Date().toISOString().slice(0, 10)}.csv`,
          `All ${allInvs.length} entered invoices`
        );
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPiExporting(false);
    }
  };

  if (loading && !d) {
    return (
      <div className="lss-card p-12 text-center text-slate-500 font-semibold">
        Loading Executive P&amp;L statement…
      </div>
    );
  }
  if (!d) return null;

  return (
    <div className="space-y-5">
      {/* ---------------- controls ---------------- */}
      <div className="lss-card p-4 flex flex-wrap items-end gap-3 print:hidden">
        <div>
          <label className="lss-overline block mb-1">Month</label>
          <input
            type="month"
            value={activeMonth || ""}
            onChange={(e) => {
              setFrom("");
              setTo("");
              if (e.target.value) pickMonth(e.target.value);
            }}
            className="lss-input px-3 py-2 text-sm font-semibold"
          />
        </div>
        <div className="flex items-center gap-2">
          <div>
            <label className="lss-overline block mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="lss-input px-3 py-2 text-sm font-semibold"
            />
          </div>
          <div>
            <label className="lss-overline block mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="lss-input px-3 py-2 text-sm font-semibold"
            />
          </div>
        </div>
        {(from || to) && (
          <button
            onClick={() => {
              setFrom("");
              setTo("");
            }}
            className="text-xs font-bold text-slate-600 hover:text-rose-700 px-2 py-2"
          >
            Clear range
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] font-bold text-slate-500 tabular hidden sm:block">{periodLabel}</span>
          <button
            onClick={() => setShowGuide((v) => !v)}
            className={`px-3 py-2 text-xs font-extrabold rounded-lg flex items-center gap-1.5 transition border ${
              showGuide
                ? "bg-amber-100 text-amber-950 border-amber-300 shadow-sm"
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
            title="Toggle calculation methodology and source columns guide"
          >
            <Calculator className="w-4 h-4 text-amber-700" />
            {showGuide ? "Hide Calculation Guide" : "Calculation Reference"}
          </button>
          <button
            onClick={exportPurchaseInvoices}
            disabled={piExporting}
            className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-2 text-xs font-extrabold rounded-lg flex items-center gap-1.5 transition shadow-xs disabled:opacity-50"
            title="Export all entered purchase invoices (itemized lines, rates & GST) to CSV"
          >
            <Download className="w-4 h-4" />
            {piExporting ? "Exporting PIs..." : "Export Purchase Invoices"}
          </button>
          <button
            onClick={exportCsv}
            className="bg-slate-950 text-white px-3 py-2 text-xs font-extrabold rounded-lg flex items-center gap-1.5 hover:bg-slate-800 transition"
          >
            <FileSpreadsheet className="w-4 h-4" /> Export Excel / CSV
          </button>
        </div>
      </div>

      {/* ---------------- KPI banner (2 rows, 4 cards each) ---------------- */}
      <div className="space-y-3">
        {/* Row 1: Sales, Concessions & Advances (4 cards) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard
            label="Net Sales"
            value={s.net_sales || 0}
            sub={`vs previous period ${mom.net_sales_pct >= 0 ? "+" : ""}${mom.net_sales_pct || 0}%`}
            delta={mom.net_sales_pct}
            tone="gold"
          />
          <KpiCard
            label="Other Discount"
            value={rev.value_card || s.value_card || s.other_discount || 0}
            sub="Paid from Value Card (tender deduction)"
            delta={mom.other_discount_pct}
            tone="rose"
          />
          <KpiCard
            label="Net Discount"
            value={rev.discounts || s.discounts || 0}
            sub="POS Total Discount (item & bill level)"
            delta={mom.discounts_pct}
            tone="amber"
            tag="Ref Only"
          />
          <KpiCard
            label="Advance"
            value={s.advance || 0}
            sub="Customer Advance balance (liability)"
            tone="sky"
            tag="Ref Only"
          />
        </div>

        {/* Row 2: Operations, Costs & Profitability (4 cards) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KpiCard
            label="COGS"
            value={s.cogs || 0}
            sub={`${pctOf(s.cogs || 0, s.net_sales)}% of Net Sales`}
            tone="slate"
          />
          <KpiCard
            label="Gross Profit"
            value={s.gross_profit || 0}
            sub={`Gross margin ${s.gross_margin_pct || 0}%`}
            tone={(s.gross_profit || 0) >= 0 ? "emerald" : "rose"}
          />
          <KpiCard
            label="Total OPEX"
            value={s.opex || 0}
            sub={`${pctOf(s.opex || 0, s.net_sales)}% of Net Sales`}
            tone="slate"
          />
          <KpiCard
            label="Net Profit"
            value={s.net_profit || 0}
            sub={`Net margin ${s.net_margin_pct || 0}%`}
            delta={mom.net_profit_pct}
            tone={(s.net_profit || 0) >= 0 ? "emerald" : "rose"}
          />
        </div>
      </div>

      {/* ---------------- Calculation Methodology & Source Column Reference ---------------- */}
      {showGuide && (
        <div className="lss-card overflow-hidden border border-amber-300 bg-gradient-to-br from-amber-50/60 via-white to-slate-50 p-5 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-amber-200/80 mb-4">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-900 border border-amber-400/30">
                <Calculator className="w-4 h-4 text-amber-800" />
              </div>
              <div>
                <h3 className="text-sm font-black uppercase tracking-wider text-slate-950">
                  P&amp;L Calculation Methodology &amp; Data Source Column Reference
                </h3>
                <p className="text-[11px] font-semibold text-slate-600">
                  Exact column mappings from POS exports, Bank Statements, and the Stock Ledger
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowGuide(false)}
              className="text-xs font-bold text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100 transition"
            >
              Dismiss
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* POS Reference */}
            <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-xs flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-1 mb-2">
                  <span className="text-[11px] font-black uppercase tracking-wider text-sky-800 bg-sky-50 px-2 py-0.5 rounded border border-sky-200">
                    1. POS Sheet / Daily Register
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">Revenue</span>
                </div>
                <div className="text-xs font-bold text-slate-900 mb-1.5">Net Sales Calculation:</div>
                <ul className="text-[11px] text-slate-600 space-y-1 font-medium">
                  <li>
                    <strong className="text-slate-800">Net Price:</strong> Column for ticket base revenue across products and services (Gross Sales basis).
                  </li>
                  <li>
                    <strong className="text-slate-800">Other (Tender):</strong> Paid from Value Card (taken as the ONLY deduction in P&amp;L; reported in KPI as Other Discount).
                  </li>
                  <li>
                    <strong className="text-slate-800">Total Discount:</strong> Bill-level discounts (already accounted for within Net Price; excluded from P&amp;L deductions).
                  </li>
                  <li>
                    <strong className="text-slate-800">Type / Category:</strong> Splits retail products vs Hair, Beauty, Mani-Pedi.
                  </li>
                  <li>
                    <strong className="text-slate-800">Advance:</strong> Customer advances ("Add to Advance balance") are unearned liabilities and excluded from Net Sales (reported in KPI as Ref Only).
                  </li>
                </ul>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] font-mono font-bold text-sky-900 bg-sky-50/60 p-2 rounded">
                Net Sales = Gross Sales (excluding Advances) − Other Discount (Paid from Value Card)
              </div>
            </div>

            {/* Stock Ledger Reference */}
            <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-xs flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-1 mb-2">
                  <span className="text-[11px] font-black uppercase tracking-wider text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    2. Stock Ledger &amp; Catalog
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">COGS</span>
                </div>
                <div className="text-xs font-bold text-slate-900 mb-1.5">Cost of Goods Sold:</div>
                <ul className="text-[11px] text-slate-600 space-y-1 font-medium">
                  <li>
                    <strong className="text-slate-800">Quantity:</strong> Column for stock-in / restock / purchases movements.
                  </li>
                  <li>
                    <strong className="text-slate-800">before_stock &amp; after_stock:</strong> Opening and closing shelf quantities.
                  </li>
                  <li>
                    <strong className="text-slate-800">Unit Price:</strong> Valuation rate from SKU catalog.
                  </li>
                  <li>
                    <strong className="text-slate-800">Account 5000s:</strong> Bank/cash vouchers for direct purchases.
                  </li>
                  <li className="mt-1 pt-1 border-t border-slate-100 text-[10px] text-emerald-900 font-semibold">
                    <strong className="text-emerald-950">Store Inventory:</strong> ₹15.31L live valuation across all 456 catalog SKUs in Inventory Hub (4,560 units).
                  </li>
                </ul>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] font-mono font-bold text-emerald-900 bg-emerald-50/60 p-2 rounded">
                TOTAL COGS = Opening Stock + Inward/Purchases − Closing Stock
              </div>
            </div>

            {/* Bank Statement & Cash Book Reference */}
            <div className="p-3.5 rounded-xl border border-slate-200 bg-white shadow-xs flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between gap-1 mb-2">
                  <span className="text-[11px] font-black uppercase tracking-wider text-rose-800 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                    3. Bank &amp; Cash Vouchers
                  </span>
                  <span className="text-[10px] font-bold text-slate-500">OPEX</span>
                </div>
                <div className="text-xs font-bold text-slate-900 mb-1.5">Operational Expenses:</div>
                <ul className="text-[11px] text-slate-600 space-y-1 font-medium">
                  <li>
                    <strong className="text-slate-800">Debit:</strong> Money-out column in bank statement.
                  </li>
                  <li>
                    <strong className="text-slate-800">Chart of Accounts:</strong> Categorized into 18 standard accounts (6000s).
                  </li>
                  <li>
                    <strong className="text-slate-800">Cash Book:</strong> Approved cash payments for petty cash &amp; store ops.
                  </li>
                  <li>
                    <strong className="text-slate-800">Credit:</strong> Refunds/reversals offset against opex.
                  </li>
                </ul>
              </div>
              <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] font-mono font-bold text-rose-900 bg-rose-50/60 p-2 rounded">
                Total OPEX = Sum of all approved 6000s debits − approved credits
              </div>
            </div>
          </div>

          <div className="mt-3.5 pt-3 border-t border-amber-200/60 flex flex-wrap items-center justify-between gap-2 text-[11px] font-bold text-slate-700">
            <div>
              <span className="text-amber-900 uppercase tracking-wider text-[10px] mr-1.5 font-black">Gross Profit:</span>
              <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-900 font-mono">Net Sales − Total COGS</code>
            </div>
            <div>
              <span className="text-amber-900 uppercase tracking-wider text-[10px] mr-1.5 font-black">Net Profit:</span>
              <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-900 font-mono">Gross Profit − Total Operational Expenses</code>
            </div>
            <div className="text-slate-500 font-semibold italic text-[10px]">
              * Check below under each statement row for its specific source column reference.
            </div>
          </div>
        </div>
      )}

      {/* Period Allocation Banner if active */}
      {periodAdjustments && (periodAdjustments.inflows.length > 0 || periodAdjustments.outflows.length > 0) && (
        <div className="lss-card px-5 py-3.5 bg-gradient-to-r from-purple-50/90 via-indigo-50/80 to-amber-50/80 border border-purple-200">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <span className="p-1.5 rounded-lg bg-purple-600 text-white shadow-2xs">
                <Calendar className="w-4 h-4" />
              </span>
              <div>
                <span className="text-xs font-black uppercase tracking-wider text-purple-950">
                  Period Allocations Applied for {formatMonthLabel(viewingMonth)}
                </span>
                <span className="block text-[11px] font-semibold text-purple-900 mt-0.5">
                  Net OPEX Adjustment:{" "}
                  <strong className={periodAdjustments.netOpexDelta >= 0 ? "text-purple-950 font-black" : "text-emerald-700 font-black"}>
                    {periodAdjustments.netOpexDelta >= 0 ? `+${money(periodAdjustments.netOpexDelta)}` : money(periodAdjustments.netOpexDelta)}
                  </strong>
                  {" "}({periodAdjustments.inflows.length} payment{periodAdjustments.inflows.length === 1 ? "" : "s"} shifted in, {periodAdjustments.outflows.length} shifted out)
                </span>
              </div>
            </div>
          </div>
          <div className="mt-2.5 pt-2 border-t border-purple-200/60 flex flex-wrap gap-2 text-[11px]">
            {periodAdjustments.inflows.map((inf, idx) => (
              <span key={`inf-${idx}`} className="px-2 py-0.5 rounded bg-white/90 border border-purple-300 font-bold text-purple-900 flex items-center gap-1 shadow-2xs">
                <span className="text-emerald-600 font-extrabold">+</span>
                <span>{money(inf.inflow_amount)}</span>
                <span className="text-slate-500 font-normal">from {formatMonthLabel(inf.original_month)} ({inf.ledger_name})</span>
              </span>
            ))}
            {periodAdjustments.outflows.map((out, idx) => (
              <span key={`out-${idx}`} className="px-2 py-0.5 rounded bg-white/90 border border-amber-300 font-bold text-amber-900 flex items-center gap-1 shadow-2xs">
                <span className="text-rose-600 font-extrabold">−</span>
                <span>{money(out.shifted_out_amount)}</span>
                <span className="text-slate-500 font-normal">shifted out ({out.ledger_name})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- statement ---------------- */}
      <div className="lss-card overflow-hidden border border-slate-200">
        <div className="px-5 py-3.5 bg-slate-950 text-white flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-black uppercase tracking-widest text-amber-300">
              Executive Profit &amp; Loss Statement
            </div>
            <div className="text-[11px] font-semibold text-slate-300 mt-0.5">{periodLabel} · figures in ₹</div>
          </div>
          <div className="text-[11px] font-bold text-slate-300">
            {d.revenue?.rows || 0} POS bills · {d.opex?.vouchers || 0} vouchers
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="lss-table min-w-[720px]">
            <thead>
              <tr>
                <th className="w-[52%]">Particulars</th>
                <th className="text-right">Amount ₹</th>
                <th className="text-right">% of Net Sales</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((i, idx) => {
                if (i.t === "section") {
                  return (
                    <tr key={idx} className="bg-amber-50/70">
                      <td colSpan={3} className="py-2.5 px-4 text-[11px] font-black uppercase tracking-widest text-amber-900 border-t border-amber-300">
                        {i.label}
                      </td>
                    </tr>
                  );
                }
                if (i.t === "memo") {
                  return (
                    <tr key={idx}>
                      <td colSpan={3} className="py-1.5 px-4 text-[11px] italic font-semibold text-slate-500 bg-slate-50/60">
                        <Info className="w-3 h-3 inline mr-1 -mt-0.5 text-slate-400" />
                        {i.label}
                      </td>
                    </tr>
                  );
                }
                if (i.t === "rate") {
                  return (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="py-2.5 px-4 pl-8 text-xs font-semibold text-slate-600">
                        {i.label}
                        <span className="block text-[10px] text-slate-400 font-medium">{i.formula}</span>
                        {i.source && (
                          <span className="block text-[10px] font-semibold text-slate-500 mt-0.5">
                            <span className="text-amber-800 font-extrabold uppercase tracking-wider text-[9px] mr-1">Source:</span>
                            {i.source}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-4 text-right tabular text-sm font-extrabold text-slate-800">{i.rate}%</td>
                      <td className="py-2.5 px-4 text-right text-[11px] text-slate-400">—</td>
                    </tr>
                  );
                }
                if (i.t === "opex") {
                  const expanded = openOpex === i.label;
                  const l = i.line || {};
                  return (
                    <React.Fragment key={idx}>
                      <tr
                        onClick={() => setOpenOpex(expanded ? null : i.label)}
                        className={`hover:bg-amber-50/50 cursor-pointer ${expanded ? "bg-amber-50/60" : ""}`}
                      >
                        <td className="py-2 px-4 text-xs font-semibold text-slate-800">
                          <span className="flex items-center gap-1.5 flex-wrap">
                            {expanded ? <ChevronDown className="w-3.5 h-3.5 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                            {i.label}
                            {l.period_adjustment && (
                              <span className={`text-[9px] font-black px-1.5 py-0.5 rounded border ${
                                l.period_adjustment > 0
                                  ? "bg-purple-100 text-purple-900 border-purple-300"
                                  : "bg-amber-100 text-amber-900 border-amber-300"
                              }`}>
                                {l.period_adjustment > 0 ? `+${money(l.period_adjustment)} shifted in` : `${money(l.period_adjustment)} shifted out`}
                              </span>
                            )}
                          </span>
                          {l.note && <span className="block text-[10px] font-medium text-slate-500">{l.note}</span>}
                          {i.source && (
                            <span className="block text-[10px] font-semibold text-slate-500 mt-0.5">
                              <span className="text-amber-800 font-extrabold uppercase tracking-wider text-[9px] mr-1">Source:</span>
                              {i.source}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-4 text-right tabular text-xs font-bold text-slate-900">{money(i.value)}</td>
                        <td className="py-2 px-4 text-right tabular text-[11px] text-slate-500">
                          {pctOf(i.value || 0, s.net_sales)}%
                        </td>
                      </tr>
                      {expanded && (
                        <tr key={`${idx}-x`} className="bg-slate-50/80">
                          <td colSpan={3} className="py-3 px-4">
                            {/* Period Allocation & Statement Line Details */}
                            {((l.period_inflows && l.period_inflows.length > 0) || (l.period_outflows && l.period_outflows.length > 0)) && (
                              <div className="mb-3 space-y-2">
                                <div className="text-[11px] font-black uppercase tracking-wider text-purple-950 flex items-center gap-1.5">
                                  <Calendar className="w-3.5 h-3.5 text-purple-700" />
                                  <span>Period-Shifted &amp; Split Bank Statement Breakdown</span>
                                </div>

                                {/* Inflows */}
                                {(l.period_inflows || []).map((inf, k) => (
                                  <div key={`pin-${k}`} className="p-3 bg-purple-50/90 border border-purple-200 rounded-xl text-xs space-y-1.5 shadow-2xs">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black bg-purple-200 text-purple-950 border border-purple-300">
                                          {inf.type === "split"
                                            ? `Split (Part ${inf.split_index} of ${inf.split_total_parts}) · Allocated from ${formatMonthLabel(inf.original_month)}`
                                            : `100% Shifted from ${formatMonthLabel(inf.original_month)}`}
                                        </span>
                                        <span className="text-slate-500 font-semibold text-[11px]">
                                          Bank Date: <strong className="text-slate-800">{inf.original_date}</strong>
                                        </span>
                                      </div>
                                      <span className="text-sm font-black text-purple-950 tabular">
                                        +{money(inf.inflow_amount)}
                                      </span>
                                    </div>
                                    <div className="font-mono text-[11px] text-slate-800 bg-white p-2 rounded-lg border border-purple-100 break-all select-all">
                                      <span className="text-[9px] font-extrabold uppercase text-purple-900 block font-sans mb-0.5">Bank Statement Narration:</span>
                                      {inf.narration}
                                    </div>
                                    {inf.note && (
                                      <div className="text-[11px] text-slate-700 font-medium">
                                        <strong className="text-slate-900">Note:</strong> {inf.note}
                                      </div>
                                    )}
                                    {inf.all_months && inf.all_months.length > 1 && (
                                      <div className="pt-1.5 border-t border-purple-200/60 text-[11px] text-purple-950 flex flex-wrap items-center gap-2">
                                        <span className="font-extrabold text-[10px] uppercase text-purple-900">Split Schedule ({money(inf.total_amount)}):</span>
                                        {inf.all_months.map((m, mi) => (
                                          <span
                                            key={mi}
                                            className={`px-2 py-0.5 rounded border text-[10px] font-bold ${
                                              m.month === viewingMonth
                                                ? "bg-purple-600 text-white border-purple-700 font-black shadow-2xs"
                                                : "bg-white text-slate-700 border-slate-300"
                                            }`}
                                          >
                                            {formatMonthLabel(m.month)}: {money(m.amount)}
                                            {m.month === viewingMonth && " (this month)"}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}

                                {/* Outflows */}
                                {(l.period_outflows || []).map((out, k) => (
                                  <div key={`pout-${k}`} className="p-3 bg-amber-50/90 border border-amber-200 rounded-xl text-xs space-y-1.5 shadow-2xs">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black bg-amber-200 text-amber-950 border border-amber-300">
                                          {out.type === "split"
                                            ? `Split Payment · Shifted out ${money(out.shifted_out_amount)} to other months`
                                            : `100% Shifted to ${out.targets?.map((t) => formatMonthLabel(t.month)).join(", ") || "other month"}`}
                                        </span>
                                        <span className="text-slate-500 font-semibold text-[11px]">
                                          Bank Date: <strong className="text-slate-800">{out.original_date}</strong>
                                        </span>
                                      </div>
                                      <span className="text-sm font-black text-rose-700 tabular">
                                        −{money(out.shifted_out_amount)}
                                      </span>
                                    </div>
                                    <div className="font-mono text-[11px] text-slate-800 bg-white p-2 rounded-lg border border-amber-100 break-all select-all">
                                      <span className="text-[9px] font-extrabold uppercase text-amber-900 block font-sans mb-0.5">Bank Statement Narration:</span>
                                      {out.narration}
                                    </div>
                                    {out.note && (
                                      <div className="text-[11px] text-slate-700 font-medium">
                                        <strong className="text-slate-900">Note:</strong> {out.note}
                                      </div>
                                    )}
                                    {out.all_months && out.all_months.length > 1 && (
                                      <div className="pt-1.5 border-t border-amber-200/60 text-[11px] text-slate-700 flex flex-wrap items-center gap-2">
                                        <span className="font-extrabold text-[10px] uppercase text-slate-800">Split Schedule ({money(out.total_amount)}):</span>
                                        {out.all_months.map((m, mi) => (
                                          <span
                                            key={mi}
                                            className={`px-2 py-0.5 rounded border text-[10px] font-bold ${
                                              m.month === viewingMonth
                                                ? "bg-amber-600 text-white border-amber-700 font-black shadow-2xs"
                                                : "bg-white text-slate-700 border-slate-300"
                                            }`}
                                          >
                                            {formatMonthLabel(m.month)}: {money(m.amount)}
                                            {m.month === viewingMonth && " (in this month)"}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Direct posted vouchers from backend */}
                            {(l.ledgers || []).filter((lg) => (lg.amount || 0) > 0 && lg.ledger_id).length > 0 && (
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                                  Direct Posted Vouchers ({d.period?.month}):
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {l.ledgers.filter((lg) => (lg.amount || 0) > 0 && lg.ledger_id).map((lg, k) => (
                                    <button
                                      key={k}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (lg.ledger_id && onDrill) onDrill(lg, i.label);
                                      }}
                                      className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white hover:border-amber-400 hover:bg-amber-50 text-slate-700 flex items-center gap-1.5 cursor-pointer shadow-2xs transition-colors"
                                      title="Show the vouchers behind this ledger"
                                    >
                                      {lg.name}
                                      <span className="tabular text-slate-900">{money(lg.amount)}</span>
                                      <span className="text-slate-400">· {lg.vouchers || 1} vouchers</span>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Fallback when neither vouchers nor allocations exist */}
                            {!((l.period_inflows && l.period_inflows.length > 0) || (l.period_outflows && l.period_outflows.length > 0)) &&
                              (l.ledgers || []).filter((lg) => (lg.amount || 0) > 0 && lg.ledger_id).length === 0 && (
                                <div className="text-[11px] font-semibold text-slate-400 italic">
                                  No vouchers or period allocations posted to this account in {d.period?.month}
                                </div>
                              )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }

                // plain / child / total rows
                const isTotal = i.t === "total";
                const val = i.deduct ? -(i.value || 0) : i.value || 0;
                const tone = i.net
                  ? (i.value || 0) >= 0
                    ? "text-emerald-800"
                    : "text-rose-700"
                  : i.deduct
                  ? "text-rose-700"
                  : "text-slate-950";
                return (
                  <tr
                    key={idx}
                    className={
                      isTotal
                        ? i.net
                          ? "bg-emerald-50 border-t-2 border-emerald-500"
                          : "bg-slate-100 border-t-2 border-slate-400"
                        : i.child
                        ? "bg-white hover:bg-slate-50"
                        : "hover:bg-slate-50"
                    }
                  >
                    <td
                      className={`py-2.5 px-4 text-xs ${
                        i.child ? "pl-10 text-slate-600" : "text-slate-800"
                      } ${isTotal || i.strong ? "font-black uppercase tracking-wide text-sm" : "font-semibold"}`}
                    >
                      <span className="flex items-center gap-2">
                        {i.toggle === "svc" && (
                          <button
                            onClick={() => setSvcOpen((v) => !v)}
                            className="text-amber-700 hover:text-amber-900 p-0.5 rounded"
                            title={svcOpen ? "Collapse" : "Expand"}
                          >
                            {svcOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        )}
                        {i.label}
                        {i.label === "Purchase / Inward Stock" && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              exportPurchaseInvoices();
                            }}
                            disabled={piExporting}
                            className="ml-2 text-[10px] font-extrabold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded px-2 py-0.5 inline-flex items-center gap-1 transition cursor-pointer shadow-2xs disabled:opacity-50"
                            title="Export all entered purchase invoices supporting this inward stock total"
                          >
                            <Download className="w-3 h-3 text-amber-700" /> Export PIs (CSV)
                          </button>
                        )}
                      </span>
                      {i.note && <span className="block text-[10px] font-medium text-slate-500">{i.note}</span>}
                      {i.source && (
                        <span className="block text-[10px] font-semibold text-slate-500 mt-0.5 normal-case font-medium">
                          <span className="text-amber-800 font-extrabold uppercase tracking-wider text-[9px] mr-1">Source:</span>
                          {i.source}
                        </span>
                      )}
                    </td>
                    <td className={`py-2.5 px-4 text-right tabular ${isTotal ? "text-base font-black" : "text-sm font-bold"} ${tone}`}>
                      {i.deduct ? `(${money(Math.abs(val))})` : money(val)}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular text-[11px] text-slate-500">
                      {i.child ? "" : `${pctOf(Math.abs(val), s.net_sales)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 text-[11px] font-semibold text-slate-500 flex flex-wrap gap-x-5 gap-y-1">
          <span>Revenue basis: net price per POS bill (owner rule, 26 Oct 2026)</span>
          <span>Deduction: only Paid from Value Card taken in P&amp;L (shown in KPI as Other Discount)</span>
          <span>Advance: Customer advances excluded from Net Sales (liability / unearned revenue, reported in KPI)</span>
          <span>COGS = Opening Stock + Purchases − Closing Stock</span>
          <span>OPEX = approved vouchers across operational expense accounts (including Security Service &amp; Internet)</span>
        </div>
      </div>

      {/* ---------------- charts (below executive profit window) ---------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="lss-card p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-black text-slate-950 uppercase tracking-wider">Revenue Mix</h3>
            <span className="text-[11px] font-semibold text-slate-500">after Paid from Value Card</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={58}
                  outerRadius={95}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {revData.map((_, i) => (
                    <Cell key={i} fill={REV_COLORS[i % REV_COLORS.length]} stroke="#fff" strokeWidth={2} />
                  ))}
                </Pie>
                <ChartTooltip
                  wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                  labelStyle={{ color: SLATE }}
                  itemStyle={{ color: SLATE, fontWeight: 700 }}
                  contentStyle={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #CBD5E1",
                    borderRadius: "10px",
                    boxShadow: "0 8px 20px rgba(15,23,42,0.18)",
                    fontSize: "12px",
                    padding: "8px 10px",
                  }}
                  formatter={(val, name) => [
                    `${money(val)}  ·  ${pctOf(val, s.net_sales)}%`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
            {revData.map((r, i) => (
              <span key={r.name} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: REV_COLORS[i % REV_COLORS.length] }} />
                {r.name}
              </span>
            ))}
          </div>
        </div>

        <div className="lss-card p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-black text-slate-950 uppercase tracking-wider">
              Operational Expense Distribution
            </h3>
            <span className="text-[11px] font-semibold text-slate-500">total {money(s.opex || 0)}</span>
          </div>
          <div className="h-64">
            {opexData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={opexData}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={58}
                    outerRadius={95}
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {opexData.map((_, i) => (
                      <Cell key={i} fill={OPEX_COLORS[i % OPEX_COLORS.length]} stroke="#fff" strokeWidth={2} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    wrapperStyle={{ zIndex: 1000, pointerEvents: "none" }}
                    labelStyle={{ color: SLATE }}
                    itemStyle={{ color: SLATE, fontWeight: 700 }}
                    contentStyle={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #CBD5E1",
                      borderRadius: "10px",
                      boxShadow: "0 8px 20px rgba(15,23,42,0.18)",
                      fontSize: "12px",
                      padding: "8px 10px",
                    }}
                    formatter={(val, name) => [`${money(val)}  ·  ${pctOf(val, s.opex)}%`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs font-semibold text-slate-400">
                No operating vouchers in this period
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
            {opexData.map((r, i) => (
              <span key={r.name} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-700">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: OPEX_COLORS[i % OPEX_COLORS.length] }} />
                {r.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
